import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import { PageHeading, SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import { SortableTh, Table, TableContent, Th } from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import useSortState from '@/hooks/useSortState';
import { FreelancerRoleResource } from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';

import FreelancerRoleFormModal from './components/FreelancerRoleFormModal';
import FreelancerRoleTableRow from './components/FreelancerRolesTableRow';

const StyledTable = styled(Table)`
  td,
  th {
    vertical-align: middle;
    font-weight: 700;
    &:first-child {
      padding-left: 24px;
    }
    &:last-child {
      width: 64px;
      padding-right: 24px;
    }
  }
`;

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const FreelancerRoles = () => {
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);

  const { sort, setSort, sortQueryParamObj } = useSortState({
    property: 'hide_from_dropdowns',
    direction: SortDirection.ASC,
  });

  const debouncedSearch = useDebounce(search, 300);

  const {
    data: roles,
    isError,
    isLoading,
  } = useQuery(
    [
      'freelancer_roles',
      {
        sort,
        search: debouncedSearch,
      },
    ],
    () =>
      api.get<never, FreelancerRoleResource[]>('/v1/freelancer-roles', {
        params: {
          ...(!!search && {
            'filter[name]': debouncedSearch,
          }),
          ...sortQueryParamObj,
        },
      }),
    {
      staleTime: 90 * 1000,
    },
  );

  return (
    <>
      <HeadingRow>
        <PageHeading>Roles</PageHeading>
        <Button onClick={() => setShowModal(true)}>Add New</Button>
      </HeadingRow>
      <SearchBar value={search} setValue={setSearch} />
      <ScrollableTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="name"
                title="Name"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="hide_from_dropdowns"
                title="Hide from dropdowns"
              />
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!roles?.length}
              colSpan={3}
            >
              {roles?.map((role) => (
                <FreelancerRoleTableRow key={role.id} role={role} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <AnimatePresence>
        {showModal && (
          <FreelancerRoleFormModal
            onClose={() => {
              setShowModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default FreelancerRoles;
